<!--
 * @FileDescription: Legal Info Page
 * @Author: jianguo Wang
 * @Date: 2025-01-07
-->
<template>
  <div class="page-wrapper">
    <headerPart />
    <main class="main-content">
      <div class="content-container">
        <router-link to="/" class="back-link">
          <svg class="back-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to Home
        </router-link>

        <article class="content-card">
          <h1 class="page-title">Legal Information</h1>

          <div class="content-body">
            <!-- Company Registration -->
            <section class="content-section">
              <h2 class="section-heading">Company Registration</h2>
              <div class="info-grid">
                <div class="info-item">
                  <p class="info-label">Legal Name</p>
                  <p class="info-value">APEXIS PTE. LTD.</p>
                </div>
                <div class="info-item">
                  <p class="info-label">Entity Type</p>
                  <p class="info-value">Singapore Private Limited Company</p>
                </div>
                <div class="info-item">
                  <p class="info-label">Entity ID</p>
                  <p class="info-value">—</p>
                </div>
                <div class="info-item">
                  <p class="info-label">Document Number</p>
                  <p class="info-value">—</p>
                </div>
              </div>
              <div class="info-item full-width">
                <p class="info-label">Registered Address</p>
                <p class="info-value">1 NORTH BRIDGE ROAD, #07-08, HIGH STREET CENTRE, SINGAPORE</p>
              </div>
            </section>

            <!-- Business Nature & Scope -->
            <section class="content-section">
              <h2 class="section-heading">Business Nature & Scope</h2>
              <p class="section-paragraph">
                APEXIS PTE. LTD. is engaged in technology services and financial solutions, providing innovative products
                and services to global customers. The company operates in compliance with all applicable laws and regulations.
              </p>
            </section>

            <!-- Regulatory Status -->
            <section class="content-section">
              <h2 class="section-heading">Regulatory Status</h2>
              <p class="section-paragraph">
                The company complies with all applicable laws and regulations related to its business operations and
                holds all necessary licenses and registration documents.
              </p>
            </section>

            <!-- Intellectual & Industrial Property -->
            <section class="content-section">
              <h2 class="section-heading">Intellectual & Industrial Property</h2>
              <p class="section-paragraph">
                The Apexis brand, logos, website design, software, and all content are owned by the company. No
                reproduction, distribution, or commercial use is permitted without authorization.
              </p>
            </section>

            <!-- Privacy & Compliance -->
            <section class="content-section">
              <h2 class="section-heading">Privacy & Compliance</h2>
              <p class="section-paragraph">
                For information about how we process personal data, please refer to our
                <router-link to="/privacy" class="inline-link">Privacy Policy</router-link>.
              </p>
            </section>

            <!-- Disclaimer -->
            <section class="content-section">
              <h2 class="section-heading">Disclaimer</h2>
              <p class="section-paragraph">
                Apexis assumes no responsibility for any damages that may arise from transactions between users and
                third parties. However, we will take necessary measures within the framework of security, privacy, and
                legal obligations.
              </p>
            </section>

            <div class="footer-meta">
              <p class="last-updated">Last Updated: January 2025</p>
            </div>
          </div>
        </article>
      </div>
    </main>
    <footerPart />
  </div>
</template>

<script>
import headerPart from "../components/header.vue";
import footerPart from "../components/footer.vue";

export default {
  name: "LegalInfoPage",
  components: {
    headerPart,
    footerPart,
  },
};
</script>

<style scoped>
.page-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.main-content {
  flex: 1;
  background: #f9fafb;
}

.content-container {
  max-width: 1024px;
  margin: 0 auto;
  padding: 48px 24px;
  text-align: left;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #4b5563;
  text-decoration: none;
  transition: color 0.2s;
  margin-bottom: 32px;
}

.back-link:hover {
  color: #8b3a3a;
}

.back-icon {
  width: 16px;
  height: 16px;
}

.content-card {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  padding: 48px;
  text-align: left;
}

.page-title {
  font-size: 30px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 32px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.content-body {
  display: flex;
  flex-direction: column;
  gap: 32px;
  color: #374151;
  line-height: 1.6;
}

.content-section {
  display: flex;
  flex-direction: column;
}

.section-heading {
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 16px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}

.info-item {
  display: flex;
  flex-direction: column;
}

.info-item.full-width {
  grid-column: 1 / -1;
}

.info-label {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 4px;
}

.info-value {
  font-size: 16px;
  font-weight: 500;
  color: #111827;
}

.section-paragraph {
  font-size: 16px;
  color: #374151;
  line-height: 1.6;
}

.inline-link {
  color: #8b3a3a;
  font-weight: 500;
  text-decoration: none;
}

.inline-link:hover {
  text-decoration: underline;
}

.footer-meta {
  padding-top: 24px;
  margin-top: 32px;
  border-top: 1px solid #e5e7eb;
}

.last-updated {
  font-size: 14px;
  color: #6b7280;
}

@media screen and (max-width: 768px) {
  .content-container {
    padding: 32px 16px;
  }

  .content-card {
    padding: 32px 24px;
  }

  .page-title {
    font-size: 24px;
  }

  .section-heading {
    font-size: 18px;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .section-paragraph {
    font-size: 14px;
  }
}
</style>
